<template>
  <modal name="modal-create-report" :adaptive="true" width="100%" height="100%">
    <div
      class="fixed top-0 left-0 flex flex-col justify-between w-screen h-screen bg-slate-200"
      :style="{ zIndex: '1000' }"
    >
      <div id="container">
        <div class="flex items-center flex-shrink-0 h-12 px-6 bg-pure-white">
          <BlBreadcrumbs :breadcrumbs="breadcrumbs" />
        </div>
        <div
          id="header"
          class="flex items-center justify-between h-16 gap-6 px-6 border-b bg-pure-white border-slate-400"
        >
          <div class="flex flex-col gap-1">
            <BlTextTitle4 text="Create a new report" />
          </div>
          <BlIcon icon="close" clickable @clicked="onClose" />
        </div>
        <div
          id="body"
          class="relative flex overflow-hidden transition-all duration-500"
          :style="{
            width: '200vw',
            transform: step === 1 ? 'translateX(0vw)' : 'translateX(-100vw) '
          }"
        >
          <div id="report-step-1" class="flex w-screen">
            <div
              class="flex flex-col items-center w-full overflow-y-auto"
              :style="{ height: 'calc(100vh - 12rem)' }"
            >
              <div class="flex flex-col gap-4 py-6 max-w-152">
                <div class="flex flex-col justify-center w-full px-10">
                  <BlTextSubtitle1 text="Select a template" />
                  <BlTextBody2
                    text="Choose how you'd like to start your report. Don't worry—you can always make changes or add content as you go."
                  />
                </div>
                <div
                  v-for="template in templateTypeOptions"
                  :key="template.value"
                  class="flex flex-col items-center justify-center w-full gap-1 px-5 py-5 text-center transition-all duration-300 border rounded-lg cursor-pointer bg-pure-white"
                  :class="{
                    'border-light-purple': templateTypeInput === template.value,
                    'border-slate-400': templateTypeInput !== template.value
                  }"
                  @click="templateTypeInput = template.value"
                >
                  <BlTextSubtitle3
                    :text="template.title"
                    :class="{
                      'text-light-purple': templateTypeInput === template.value,
                      'text-slate-700': templateTypeInput !== template.value
                    }"
                  />
                  <BlTextBody3 :text="template.description" class="whitespace-pre-line" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col items-center flex-shrink-0 overflow-y-auto w-152 bg-slate-300"
              :style="{ height: 'calc(100vh - 12rem)' }"
            >
              <div v-if="templateTypeInput" class="py-8 w-132">
                <ReportTemplateExample :template="templateTypeInput" />
              </div>
            </div>
          </div>
          <div id="report-step-2" class="flex w-screen">
            <div
              class="flex flex-col items-center w-full overflow-y-auto"
              :style="{ height: 'calc(100vh - 12rem)' }"
            >
              <div class="flex flex-col gap-4 py-6 max-w-152">
                <div class="flex flex-col justify-center w-full px-10">
                  <BlTextSubtitle1 text="Modify the selected template" />
                  <div v-if="templateTypeInput">
                    <BlTextBody2 text="You have selected " />
                    <BlTextBody2
                      :text="
                        templateTypeOptions.find(
                          i => i.value === templateTypeInput
                        ).title
                      "
                      class="font-bold"
                    />
                    <BlTextBody2
                      text="template for your report. Set the settings below to start building your report."
                    />
                  </div>
                </div>

                <div
                  id="general-input"
                  class="flex flex-col gap-6 px-10 py-10 border bg-pure-white border-slate-400 rounded-3xl"
                >
                  <div class="flex flex-col gap-1">
                    <BlTextSubtitle1 text="Set report name" />
                    <BlTextBody2
                      text="Set the name for your report, and it will be shown as the report's title."
                    />
                    <BlInputText :input="nameInput" @changed="inputHandler" />
                  </div>
                  <div
                    v-if="
                      templateTypeInput !== 'selected_curricula_or_target_types'
                    "
                    class="flex flex-col gap-1"
                  >
                    <BlTextSubtitle1 text="Set date range" />
                    <BlTextBody2
                      v-if="templateTypeInput === 'blank'"
                      text="Set the date range for this report, and we'll recommend targets that have made progress during this period, which you can view in the graph section later."
                    />
                    <BlTextBody2
                      v-else
                      text="Define the date range to automatically pull in graphs from all targets that have made progress within this timeframe. Targets that have not made any progress during this period will not be included."
                    />
                    <div class="flex items-center gap-4">
                      <BlInputDate
                        :input="startDateInput"
                        @changed="inputHandler"
                      />
                      <BlInputDate
                        :input="endDateInput"
                        @changed="inputHandler"
                      />
                    </div>
                  </div>
                </div>

                <div
                  v-if="
                    templateTypeInput === 'selected_curricula_or_target_types'
                  "
                  id="selection-input"
                  class="flex flex-col gap-6 px-10 py-10 border bg-pure-white border-slate-400 rounded-3xl"
                >
                  <div class="flex flex-col gap-1">
                    <BlTextSubtitle1
                      text="Selection type: curricula or data collection methods"
                    />
                    <BlTextBody2
                      text="Choose whether to include targets based on specific curricula or data collection methods. This selection will define which target graphs are included in your report."
                    />
                    <div class="grid grid-cols-2 gap-4">
                      <div
                        v-for="selection in selectionTypeOptions"
                        :key="selection.value"
                        class="flex items-center justify-center text-center border rounded cursor-pointer h-15"
                        :class="[
                          selectionTypeInput === selection.value
                            ? 'border-light-purple'
                            : 'border-slate-400'
                        ]"
                        @click="selectionTypeInput = selection.value"
                      >
                        <BlTextSubtitle3
                          :text="selection.label"
                          :class="[
                            selectionTypeInput === selection.value
                              ? 'text-light-purple'
                              : 'text-slate-700'
                          ]"
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="selectionTypeInput === 'by_curricula'"
                    class="flex flex-col gap-1"
                  >
                    <BlTextSubtitle1 text="Select curricula" />
                    <BlTextBody2
                      text="Choose the curricula that will determine which target graphs are included in your report."
                    />
                    <div
                      class="flex flex-col gap-2 px-2 py-2 rounded bg-slate-300"
                    >
                      <div class="flex items-center justify-between">
                        <BlTextBody2
                          :text="
                            `${curriculumIdsInput.length} curricula selected`
                          "
                        />
                        <BlInputButton
                          type="outlined"
                          dense
                          label="Add curricula"
                          @clicked="openCurriculum"
                        />
                      </div>
                      <div
                        v-if="curriculumIdsInput.length"
                        class="grid grid-cols-2 gap-2"
                      >
                        <div
                          v-for="id in curriculumIdsInput"
                          :key="id"
                          class="flex items-center justify-between h-10 px-2 rounded bg-pure-white"
                        >
                          <div class="flex items-center gap-2 truncate">
                            <div
                              class="flex-shrink-0 w-4 h-4 rounded-full"
                              :style="{
                                backgroundColor:
                                  curriculumOptions.find(
                                    i => i.curriculum_id === id
                                  ).curriculum_color || 'grey'
                              }"
                            ></div>
                            <BlTextBody2
                              :text="
                                curriculumOptions.find(
                                  i => i.curriculum_id === id
                                ).curriculum_name
                              "
                              class="truncate"
                            />
                          </div>
                          <BlIcon
                            icon="close"
                            clickable
                            @clicked="onRemoveCurriculum(id)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="selectionTypeInput === 'by_data_collection_methods'"
                    class="flex flex-col gap-1"
                  >
                    <BlTextSubtitle1 text="Select data collection methods" />
                    <BlTextBody2
                      text="Choose the data collection methods that will determine which target graphs are included in your report."
                    />
                    <div
                      v-for="type in filteredTypeOptions"
                      :key="type.value"
                      class="flex items-center h-10 gap-2 px-4 border rounded border-grey-200"
                    >
                      <BlInputCheck
                        :checked="typesInput.includes(type.value)"
                        @changed="onCheckType(type.value)"
                      />
                      <BlTextBody2 :text="type.label" />
                    </div>
                  </div>
                </div>

                <div
                  v-if="
                    templateTypeInput === 'progress_highlights' ||
                      templateTypeInput === 'selected_curricula_or_target_types'
                  "
                  id="status-input"
                  class="flex flex-col gap-1 px-10 py-10 border bg-pure-white border-slate-400 rounded-3xl"
                >
                  <BlTextSubtitle1 text="Filter targets by current status" />
                  <BlTextBody2
                    text="Choose which targets to hide in your report based on their current status. If you uncheck a status in this step, you can always include those targets again when building your report later."
                  />
                  <div
                    v-for="status in statusOptions"
                    :key="status.value"
                    class="flex items-center h-10 gap-2 px-4 border rounded border-grey-200"
                  >
                    <BlInputCheck
                      :checked="statusesInput.includes(status.value)"
                      @changed="onCheckStatus(status.value)"
                    />
                    <BlChip
                      :chip="
                        status.value === 'in_progress'
                          ? 'in_acquisition'
                          : status.value
                      "
                    />
                  </div>
                </div>

                <div
                  v-if="
                    templateTypeInput === 'selected_curricula_or_target_types'
                  "
                  id="limit-date-input"
                  class="flex flex-col gap-1 px-10 py-10 border bg-pure-white border-slate-400 rounded-3xl"
                >
                  <BlTextSubtitle1 text="Limit date range for graphs" />
                  <BlTextBody2
                    text="Limit the date range for your graphs to highlight progress within a specific timeframe. This ensures that only relevant progress is shown, making the graphs more legible."
                  />
                  <div class="flex items-center gap-4">
                    <BlInputDate
                      :input="startDateInput"
                      @changed="inputHandler"
                    />
                    <BlInputDate
                      :input="endDateInput"
                      @changed="inputHandler"
                    />
                  </div>
                </div>

                <div
                  v-if="templateTypeInput !== 'blank'"
                  id="includes-description-input"
                  class="flex flex-col gap-1 px-10 py-10 border bg-pure-white border-slate-400 rounded-3xl"
                >
                  <div class="flex items-center">
                    <div
                      :style="{ transform: 'scale(1.375)' }"
                      class="pl-2 pr-4"
                    >
                      <BlInputToggle
                        :checked="includeTargetDescriptionInput"
                        @changed="
                          includeTargetDescriptionInput = !includeTargetDescriptionInput
                        "
                      />
                    </div>
                    <BlTextSubtitle1
                      text="Include target description on the graph"
                    />
                  </div>
                  <BlTextBody2
                    text="By turning on this option, we will include each target's description directly on the graphs in your report to provide additional context. You can choose to remove these descriptions later when building your report."
                  />
                </div>

                <div
                  v-if="templateTypeInput !== 'blank'"
                  id="narative-language-input"
                  class="flex flex-col gap-1 px-10 py-10 border bg-pure-white border-slate-400 rounded-3xl"
                >
                  <BlTextSubtitle1 text="Select narrative language" />
                  <BlTextBody2
                    text="Choose the language for the automatically generated narratives in your report, available in English or Indonesian. This setting does not affect the target descriptions, as they remain in the language they were originally written."
                  />
                  <div
                    class="flex items-center h-10 gap-2 px-4 border rounded border-grey-200"
                  >
                    <BlInputRadio
                      name="narative_language"
                      :checked="narativeLanguageInput === 'en'"
                      @changed="narativeLanguageInput = 'en'"
                    />
                    <BlTextBody2 text="English" />
                  </div>
                  <div
                    class="flex items-center h-10 gap-2 px-4 border rounded border-grey-200"
                  >
                    <BlInputRadio
                      name="narative_language"
                      :checked="narativeLanguageInput === 'id'"
                      @changed="narativeLanguageInput = 'id'"
                    />
                    <BlTextBody2 text="Indonesian" />
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="templateTypeInput !== 'blank'"
              id="targets-preview"
              class="flex flex-col flex-shrink-0 overflow-y-auto border-l w-116 bg-pure-white border-slate-400"
              :style="{ height: 'calc(100vh - 12rem)' }"
            >
              <div
                class="sticky top-0 z-10 flex flex-col gap-1 px-6 py-6 border-b bg-pure-white border-slate-400"
              >
                <BlTextSubtitle1 text="Targets to be added to the report" />
                <div>
                  <BlTextBody2 text="Graph(s) from " />
                  <BlTextBody2
                    :text="`${targetsPreview.length} `"
                    class="font-bold"
                  />
                  <BlTextBody2
                    text="target(s) will be included in your report based on the settings."
                  />
                </div>
              </div>
              <div
                v-if="fetchTargetLoading"
                class="grid h-full place-items-center"
              >
                <div class="blum-loader"></div>
              </div>
              <div
                v-else-if="!targetsPreview.length"
                class="grid h-full px-16 text-center place-items-center"
              >
                <BlTextBody2
                  text="No targets are available. Set the date range to see which targets will be included in your report."
                />
              </div>
              <div v-else>
                <div
                  v-for="target in targetsPreview"
                  :key="target.id"
                  class="relative flex border-b h-28 border-slate-400"
                >
                  <div
                    class="h-full w-1.5"
                    :style="{ backgroundColor: target.curriculum_color }"
                  ></div>
                  <div class="flex flex-col gap-1 px-6 py-4 w-112">
                    <div class="flex">
                      <BlChip
                        :chip="
                          target.status === 'in_progress'
                            ? 'in_acquisition'
                            : target.status
                        "
                      />
                    </div>
                    <BlTextBody2
                      :text="target.curriculum_name"
                      class="truncate"
                    />
                    <BlTextSubtitle3 :text="target.name" class="truncate" />
                  </div>
                </div>
                <div class="grid px-16 text-center h-28 place-items-center">
                  <BlTextBody2 text="End of list." />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        id="footer"
        class="sticky bottom-0 z-20 flex items-center justify-between h-20 px-6 border-t border-slate-500 bg-pure-white"
      >
        <div
          class="absolute left-0 z-10 w-screen h-10 border-b white-overlay bottom-full border-slate-500"
        ></div>
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-1">
            <div class="flex-shrink-0 w-20 h-1 rounded bg-light-purple" />
            <div
              class="flex-shrink-0 w-20 h-1 rounded"
              :class="[step === 2 ? 'bg-light-purple' : 'bg-slate-400']"
            />
          </div>
          <div class="flex gap-1">
            <div
              v-if="(!templateTypeInput && step === 1) || step === 2"
              class="relative flex items-center justify-center w-5 h-5 border rounded-full border-slate-600"
            >
              <BlTextSubtitle4 :text="step" />
            </div>
            <div
              v-else
              class="relative flex items-center justify-center w-5 h-5 border rounded-full border-success-400"
            >
              <BlIcon icon="check" color="success-4" class="w-6 h-6" />
            </div>
            <div class="flex h-5">
              <BlTextSubtitle3
                :text="
                  step === 1
                    ? 'Select a template'
                    : 'Modify the selected template'
                "
              />
            </div>
          </div>
        </div>
        <div class="flex items-center gap-4">
          <BlInputButton
            v-if="step === 1"
            class="flex-shrink-0"
            type="outlined"
            :disabled="!templateTypeInput"
            label="Next: Modify selected template"
            @clicked="step = 2"
          />
          <BlInputButton
            v-if="step === 2"
            class="flex-shrink-0"
            type="outlined"
            :label="`Back`"
            @clicked="step = 1"
          />
          <BlInputButton
            v-if="step === 2"
            class="flex flex-shrink-0 truncate max-w-80"
            :disabled="isDisabledSubmit"
            :loading="submitLoading"
            :label="
              templateTypeInput === 'blank'
                ? 'Generate report'
                : `Generate report with ${targetsPreview.length} target(s)`
            "
            @clicked="onSubmit"
          />
        </div>
      </div>
    </div>

    <BlBackdrop
      v-if="curriculumOpened"
      title="Select curricula"
      label="Save"
      description="We'll include all targets from the selected curricula that this client has."
      @backdrop-close="closeCurriculum({ save: false })"
      @backdrop-action="closeCurriculum({ save: true })"
    >
      <div class="px-8 overflow-y-auto" :style="{ maxHeight: 'calc(100vh - 16rem)'}">
        <div
          v-for="curriculum in curriculumOptions"
          :key="curriculum.curriculum_id"
          class="flex items-center justify-between h-12 gap-2 px-4 border-b w-140 border-slate-400"
        >
          <div class="flex items-center gap-2 truncate">
            <BlInputCheck
              :checked="curriculumIdsModal.includes(curriculum.curriculum_id)"
              @changed="onCheckCurriculum(curriculum.curriculum_id)"
            />
            <BlTextBody2 :text="curriculum.curriculum_name" class="truncate" />
          </div>
          <div class="flex-shrink-0">
            <BlTextBody2 :text="`${curriculum.total_targets} target(s)`" />
          </div>
        </div>
      </div>
      <div class="absolute z-10 left-8 -bottom-12">
        <BlTextBody2
          :text="`${curriculumIdsModal.length} curricula selected`"
        />
      </div>
    </BlBackdrop>
  </modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import ReportTemplateExample from "./TemplateExample.vue";

import BlBreadcrumbs from "../../BlBreadcrumbs.vue";
import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextSubtitle4 from "../../BlTextSubtitle4.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputDate from "../../BlInputDate.vue";
import BlInputCheck from "../../BlInputCheck.vue";
import BlInputToggle from "../../BlInputToggle.vue";
import BlInputRadio from "../../BlInputRadio.vue";
import BlIcon from "../../BlIcon.vue";
import BlChip from "../../BlChip.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "NewReportModal",
  components: {
    ReportTemplateExample,
    BlBreadcrumbs,
    BlTextTitle4,
    BlTextSubtitle1,
    BlTextSubtitle3,
    BlTextSubtitle4,
    BlTextBody2,
    BlTextBody3,
    BlInputButton,
    BlInputText,
    BlInputDate,
    BlInputCheck,
    BlInputToggle,
    BlInputRadio,
    BlIcon,
    BlChip,
    BlBackdrop
  },
  props: {
    // @close-modal
  },
  data() {
    return {
      submitLoading: false,
      step: 1, // 1 | 2
      nameInput: {
        placeholder: "Type report name",
        model: ""
      },
      startDateInput: { model: "" },
      endDateInput: { model: "" },
      includeHeaderInput: true,
      templateTypeInput: "", // progress_highlights | mastered_targets | selected_curricula_or_target_types | blank
      templateTypeOptions: [
        {
          value: "progress_highlights",
          title: "Progress highlights report",
          description: `Automatically includes all graphs from targets that showed progress during the selected date range. Perfect for tracking ongoing development without manual input.`
        },
        {
          value: "mastered_targets",
          title: "Mastered targets report",
          description: `Includes only graphs from targets that reached mastery within the chosen timeframe. Ideal for showcasing all mastered targets.`
        },
        {
          value: "selected_curricula_or_target_types",
          title: "Report by selected curricula or data collection methods",
          description: `Provides the option to add all graphs from targets based on the selected curricula or specific data collection methods.`
        },
        {
          value: "blank",
          title: "Blank report",
          description: `Begin with an empty report format, allowing complete flexibility to add graphs from any target you choose. Perfect for personalized reporting.`
        }
      ],
      selectionTypeInput: "", // by_curricula | by_data_collection_methods
      selectionTypeOptions: [
        { value: "by_curricula", label: "By curricula" },
        {
          value: "by_data_collection_methods",
          label: "By data collection methods"
        }
      ],
      statusesInput: [
        "pending",
        "in_progress",
        "paused",
        "mastered",
        "discontinued"
      ], // pending | in_progress | paused | mastered | discontinued
      statusOptions: [
        { value: "mastered", label: "Mastered" },
        { value: "in_progress", label: "In acquisition" },
        { value: "paused", label: "Paused" },
        { value: "discontinued", label: "Discontinued" },
        { value: "pending", label: "Pending" }
      ],
      includeTargetDescriptionInput: false,
      narativeLanguageInput: "en", // en | id
      curriculumIdsInput: [],
      curriculumOptions: [],
      curriculumOpened: false,
      curriculumIdsModal: [],
      typesInput: [], // Target::Percentage | Target::Duration | Target::Pir | Target::Frequency | Target::Prompting
      typeOptions: [
        { value: "Target::Percentage", label: "Percentage" },
        { value: "Target::Duration", label: "Duration" },
        { value: "Target::Pir", label: "Partial Interval Recording" },
        { value: "Target::Prompting", label: "Prompting" },
        { value: "Target::Frequency", label: "Frequency" },
        { value: "Target::Sbt", label: "Skill-based Treatment" }
      ],
      //
      fetchTargetLoading: false,
      targetsPreview: []
    };
  },
  computed: {
    ...mapGetters(["client", "currentCenter"]),
    breadcrumbs() {
      const clientId = Number(this.$route.params.id);
      const clientLabel =
        (this.client && this.client.id) !== clientId
          ? "Client name"
          : this.client.name;
      return [
        { label: "Clients", target: `/clients` },
        { label: clientLabel, action: () => this.onClose(), truncate: true },
        { label: "Create report", target: `#` }
      ];
    },
    targetPreviewParams() {
      if (this.templateTypeInput === "selected_curricula_or_target_types") {
        if (!this.selectionTypeInput) return false;
        if (this.selectionTypeInput === "by_curricula") {
          if (!this.curriculumIdsInput.length) return false;
        }
        if (this.selectionTypeInput === "by_data_collection_methods") {
          if (!this.typesInput.length) return false;
        }
      } else {
        if (!this.startDateInput.model || !this.endDateInput.model) {
          return false;
        }
      }
      let params = `?start_date=${this.startDateInput.model}&end_date=${this.endDateInput.model}`;
      params += `&template_type=${this.templateTypeInput}`;
      if (this.templateTypeInput === "progress_highlights") {
        params += `&status=${this.statusesInput.join(",")}`;
      }
      if (this.templateTypeInput === "selected_curricula_or_target_types") {
        params += `&status=${this.statusesInput.join(",")}`;
        if (this.selectionTypeInput === "by_curricula") {
          params += `&curriculum_ids=${this.curriculumIdsInput.join(",")}`;
        }
        if (this.selectionTypeInput === "by_data_collection_methods") {
          params += `&type=${this.typesInput.join(",")}`;
        }
      }
      return params;
    },
    isDisabledSubmit() {
      if (!this.nameInput.model) return true;
      if (!this.startDateInput.model || !this.endDateInput.model) return true;
      if (this.templateTypeInput === "selected_curricula_or_target_types") {
        if (!this.selectionTypeInput) return true;
        if (this.selectionTypeInput === "by_curricula") {
          if (!this.curriculumIdsInput.length) return true;
        }
        if (this.selectionTypeInput === "by_data_collection_methods") {
          if (!this.typesInput.length) return true;
        }
      }
      return false;
    },
    filteredTypeOptions() {
      return this.typeOptions.filter(
        type => type.value !== "Target::Sbt" || this.currentCenter.enable_sbt
      );
    }
  },
  watch: {
    templateTypeInput(val) {
      this.selectionTypeInput = "";
      this.statusesInput = [
        "pending",
        "in_progress",
        "paused",
        "mastered",
        "discontinued"
      ];
      this.curriculumIdsInput = [];
      this.typesInput = [];
      this.targetsPreview = [];
      if (val === "selected_curricula_or_target_types") {
        this.fetchCurricula();
      }
    },
    targetPreviewParams(val) {
      if (val) this.fetchTargetsPreview();
      else this.targetsPreview = [];
    }
  },
  mounted() {},
  methods: {
    ...mapActions(["actionGet", "createReport"]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    openCurriculum() {
      this.curriculumIdsModal = [...this.curriculumIdsInput];
      this.curriculumOpened = true;
    },
    closeCurriculum({ save }) {
      if (save) this.curriculumIdsInput = [...this.curriculumIdsModal];
      this.curriculumOpened = false;
    },
    onRemoveCurriculum(val) {
      this.curriculumIdsInput = this.curriculumIdsInput.filter(i => i !== val);
    },
    onCheckCurriculum(val) {
      if (this.curriculumIdsModal.includes(val)) {
        this.curriculumIdsModal = this.curriculumIdsModal.filter(
          i => i !== val
        );
      } else {
        this.curriculumIdsModal.push(val);
      }
    },
    onCheckType(val) {
      if (this.typesInput.includes(val)) {
        this.typesInput = this.typesInput.filter(i => i !== val);
      } else {
        this.typesInput.push(val);
      }
    },
    onCheckStatus(val) {
      if (this.statusesInput.includes(val)) {
        this.statusesInput = this.statusesInput.filter(i => i !== val);
      } else {
        this.statusesInput.push(val);
      }
    },
    fetchCurricula() {
      const data = {
        url: `/api/v1/clients/${this.client.id}/curricula`
      };
      this.actionGet(data).then(({ success, data }) => {
        if (!success) return;
        this.curriculumOptions = data || [];
      });
    },
    fetchTargetsPreview() {
      const params = this.targetPreviewParams;
      const data = {
        url: `/api/v1/clients/${this.client.id}/reports/display_targets${params}`
      };
      this.fetchTargetLoading = true;
      this.targetsPreview = [];
      this.actionGet(data).then(({ success, data }) => {
        this.fetchTargetLoading = false;
        if (!success) return;
        this.targetsPreview = data || [];
      });
    },
    onClose() {
      this.step = 1;
      this.templateTypeInput = "";
      this.$modal.hide("modal-create-report");
    },
    onSubmit() {
      const data = {
        client_id: this.client.id,
        body: {
          report: {
            name: this.nameInput.model,
            start_date: this.startDateInput.model,
            end_date: this.endDateInput.model,
            include_header: this.includeHeaderInput
          },
          template_type: this.templateTypeInput, // progress_highlights | mastered_targets | selected_curricula_or_target_types | blank
          status: "", // pending | in_progress | paused | mastered | discontinued -- only for progress_highlights & selected_curricula_or_target_types -- status separated by comma if more than one
          include_target_description: this.includeTargetDescriptionInput, // boolean
          curriculum_ids: "", // by_curricula -- curriculum ids separated by comma if more than one -- only for selected_curricula_or_target_types
          type: "", // by_data_collection_methods -- Target::Percentage | Target::Duration | Target::Pir | Target::Frequency | Target::Prompting -- only for selected_curricula_or_target_types -- target type separated by comma if more than one
          language: this.narativeLanguageInput
        }
      };
      if (this.templateTypeInput === "progress_highlights") {
        data.body.status = this.statusesInput.join(",");
      }
      if (this.templateTypeInput === "selected_curricula_or_target_types") {
        data.body.status = this.statusesInput.join(",");
        if (this.selectionTypeInput === "by_curricula") {
          data.body.curriculum_ids = this.curriculumIdsInput.join(",");
        }
        if (this.selectionTypeInput === "by_data_collection_methods") {
          data.body.type = this.typesInput.join(",");
        }
      }
      if (this.templateTypeInput === "blank") {
        data.body.template_type = null;
      }
      this.submitLoading = true;
      this.createReport(data).then(({ success, data }) => {
        this.submitLoading = false;
        if (!success) return;
        this.templateTypeInput = "";
        this.$modal.hide("modal-create-report");
        this.$router.push(
          `/clients/${this.client.id}/report/${data.slug}/edit`
        );
      });
    }
  }
};
</script>

<style scoped lang="scss">
.white-overlay {
  background: linear-gradient(transparent, #ffffff);
}
</style>



// WEBPACK FOOTER //
// src/components/clients/report/NewReportModal.vue